import { Chip, Grid } from "@material-ui/core";
import { flatten, noop } from "lodash";
import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { from } from "rxjs";
import { map, mergeAll } from "rxjs/operators";
import { Option } from "space-monad";
import styled from "styled-components";
import { Button } from "../../../../components/button";
import { Country } from "../../../../components/forms/country";
import { DateField } from "../../../../components/forms/date";
import { Select } from "../../../../components/forms/select";
import { TextField } from "../../../../components/forms/text";
import { AllImages } from "../../../../components/image/all-images-context";
import { GlobalOverlayContext } from "../../../../components/overlay/context";
import {
  countries,
  useFaceAttachmentType,
} from "../../../../store/common/models";
import {
  AddressAttachmentType,
  IdentityAttachmentType,
  useAddressAttachmentType,
  useIdentityAttachmentType,
} from "../../../../store/common/models/index";
import { VerificationDTO } from "../../../../store/verification/models/dto/kyc";
import {
  ServiceType,
  useAddressFileCategory,
  useFaceFileCategory,
  useIdentityFileCategory,
} from "../../../../store/verification/models/internal";
import { VerificationService } from "../../../../store/verification/service";
import { mapObject } from "../../../../utils/utils";
import { ApprovalModal } from "./approval-modal";
import { CanEditKyc } from "./can-edit-kyc";
import {
  ChipContainer,
  Label,
  ServiceDetail,
  StyledWidget,
} from "./components";
import { SingleKycContext } from "./context";
import { Images } from "./images";

const LeftGridItem = styled(Grid).attrs({ item: true })`
  display: flex;
  justify-content: flex-end;
`;

const RememberToSave = styled.div`
  text-align: right;
  font-style: italic;
  margin-top: 0.5em;
  opacity: 0.7;
  margin-right: 10px;
`;

interface IChecksPerformedProps {
  idDocType: IdentityAttachmentType | undefined;
  setIdDocType: Dispatch<SetStateAction<IdentityAttachmentType | undefined>>;
  addDocType: AddressAttachmentType | undefined;
  setAddDocType: Dispatch<SetStateAction<AddressAttachmentType | undefined>>;

  item: VerificationDTO.Responses.GetSingleKyc.T;
  fraudCheckId: string;

  isEditing: boolean;
}

const ChecksPerformed: FunctionComponent<IChecksPerformedProps> = ({
  addDocType,
  idDocType,
  item,
  setAddDocType,
  setIdDocType,
  isEditing,
  fraudCheckId,
}) => {
  const { setOverlay } = useContext(GlobalOverlayContext);

  const form = useFormContext();

  const {
    user: { address, identityDocument },
    serviceDetails,
    consentNote,
  } = item;

  const onIdentityOverlay = useCallback(
    () =>
      setOverlay({
        title: "Update KYC Status - Identity",
        children: <ApprovalModal service="DOCUMENT" />,
      }),
    []
  );

  const onAddressOverlay = useCallback(
    () =>
      setOverlay({
        title: "Update KYC Status - Address",
        children: <ApprovalModal service="ADDRESS" />,
      }),
    []
  );

  const onFaceOverlay = useCallback(
    () =>
      setOverlay({
        title: "Update KYC Status - Face",
        children: <ApprovalModal service="FACE" />,
      }),
    []
  );

  const onConsentOverlay = useCallback(
    () =>
      setOverlay({
        title: "Update KYC Status - Consent",
        children: <ApprovalModal service="CONSENT" />,
      }),
    []
  );
  const onBackgroundChecksOverlay = useCallback(
    () =>
      setOverlay({
        title: "Update KYC Status - Background checks",
        children: <ApprovalModal service="BACKGROUND_CHECKS" />,
      }),
    []
  );

  const IdentityFileType = useIdentityAttachmentType();
  const IdentityFileCategory = useIdentityFileCategory();

  const AddressFileType = useAddressAttachmentType();
  const AddressFileCategory = useAddressFileCategory();

  const FaceFileType = useFaceAttachmentType();
  const FaceFileCategory = useFaceFileCategory();

  const { changedServices } = useContext(SingleKycContext);
  const documentChanged = useMemo(() => changedServices.includes("DOCUMENT"), [
    changedServices,
  ]);

  const addressChanged = useMemo(() => changedServices.includes("ADDRESS"), [
    changedServices,
  ]);

  const faceChanged = useMemo(() => changedServices.includes("FACE"), [
    changedServices,
  ]);

  const backgroundChecksChanged = useMemo(
    () => changedServices.includes("BACKGROUND_CHECKS"),
    [changedServices]
  );
  const consentChanged = useMemo(() => changedServices.includes("CONSENT"), [
    changedServices,
  ]);

  const images = useMemo(
    () => flatten(serviceDetails.map((i) => i.details.documents)),
    [serviceDetails]
  );

  const { setFiles } = useContext(AllImages);
  useEffect(() => {
    const sub = from(images)
      .pipe(
        map((image) =>
          VerificationService.getKycDocument({
            docId: image.id,
            kycId: item.fraudCheckId,
          }).then((o) => o.map((x) => ({ file: x, ...image })))
        ),
        mergeAll()
      )
      .subscribe((option) => {
        option.fold(noop, (x) => {
          setFiles((y) => {
            return y
              .filter((z) => z.id !== x.id)
              .concat([x])
              .map((r) => {
                let sideView;

                if (
                  serviceDetails
                    .find((x) => x.service === "DOCUMENT")
                    ?.details.documents.find((d) => d.id === r.id)
                ) {
                  sideView = (
                    <div
                      style={{
                        display: "grid",
                        gap: 10,
                        gridTemplateColumns: "1fr 1fr",
                      }}
                    >
                      <strong>Document Number</strong>
                      <div>{identityDocument?.number ?? "n/a"}</div>
                      <strong>Issue Date:</strong>
                      <div>
                        {identityDocument?.issueDate
                          ?.local()
                          .format("yyyy-MM-DD") ?? "n/a"}
                      </div>
                      <strong>Expiry Date: </strong>
                      <div>
                        {identityDocument?.expiryDate
                          ?.local()
                          .format("yyyy-MM-DD") ?? "n/a"}
                      </div>
                      <strong>Document Type: </strong>
                      <div>
                        {idDocType
                          ? IdentityFileType.enum[
                              idDocType as IdentityAttachmentType
                            ]
                          : "n/a"}
                      </div>
                    </div>
                  );
                } else if (
                  serviceDetails
                    .find((x) => x.service === "ADDRESS")
                    ?.details.documents.find((d) => d.id === r.id)
                ) {
                  sideView = (
                    <div
                      style={{
                        display: "grid",
                        gap: 10,
                        gridTemplateColumns: "1fr 1fr",
                      }}
                    >
                      <strong>Street:</strong>
                      <div>{address?.streetAddress}</div>
                      <strong>City:</strong>
                      <div>{address?.city}</div>
                      <strong>State:</strong>
                      <div>{address?.state}</div>
                      <strong>Country:</strong>
                      <div>
                        {address?.country
                          ? countries.enum[
                              address.country as typeof countries.T
                            ]
                          : null}
                      </div>
                      <strong>ZIP:</strong>
                      <div>{address?.zip}</div>
                      <strong>Document Type:</strong>
                      <div>
                        {addDocType
                          ? AddressFileType.enum[
                              addDocType as AddressAttachmentType
                            ]
                          : "n/a"}
                      </div>
                    </div>
                  );
                }

                return { ...r, sideView };
              });
          });
        });
      });
    return () => {
      setFiles([]);
      sub.unsubscribe();
    };
  }, [images, item, identityDocument, idDocType]);

  return (
    <Grid container spacing={3}>
      {Option(serviceDetails)
        .map((x) => x.find(({ service }) => service === "DOCUMENT"))
        .map(({ details, service }) =>
          details.status === "NA" ? null : (
            <Grid xs={12} item key={service}>
              <ServiceDetail status={details.status} changed={documentChanged}>
                <Grid container spacing={3}>
                  <Grid item xs={3}>
                    <Label>{ServiceType.enum[service]}:</Label>
                  </Grid>
                  <Grid item xs={9}>
                    {details.status === "DECLINED" ? (
                      <ChipContainer>
                        {details.declineCodes.map(({ message }, j) => (
                          <Chip key={j} label={message} />
                        ))}
                      </ChipContainer>
                    ) : (
                      <>{details.status === "VERIFIED" ? "OK" : "PENDING"}</>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <StyledWidget title="Uploaded Documents">
                      <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                          <Grid container spacing={3}>
                            <Grid item xs={6} lg={3}>
                              <Label>Document Number:</Label>
                            </Grid>
                            <Grid item xs={6} lg={9}>
                              {isEditing ? (
                                <Controller
                                  control={form.control}
                                  name="identity.number"
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => (
                                    <TextField
                                      {...field}
                                      error={!!error}
                                      helperText={error?.message}
                                      fullWidth
                                      type="text"
                                      label="Identity Document Number"
                                    />
                                  )}
                                />
                              ) : (
                                identityDocument?.number ?? "n/a"
                              )}
                            </Grid>
                            <Grid item xs={6} lg={3}>
                              <Label>Issue Date:</Label>
                            </Grid>
                            <Grid item xs={6} lg={9}>
                              {isEditing ? (
                                <Controller
                                  control={form.control}
                                  name="identity.issueDate"
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => (
                                    <DateField
                                      {...field}
                                      error={!!error}
                                      helperText={error?.message}
                                      fullWidth
                                      type="text"
                                      label="Issue Date"
                                    />
                                  )}
                                />
                              ) : (
                                identityDocument?.issueDate
                                  ?.local()
                                  .format("yyyy-MM-DD") ?? "n/a"
                              )}
                            </Grid>
                            <Grid item xs={6} lg={3}>
                              <Label>Expiry Date:</Label>
                            </Grid>
                            <Grid item xs={6} lg={9}>
                              {isEditing ? (
                                <Controller
                                  control={form.control}
                                  name="identity.expiryDate"
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => (
                                    <DateField
                                      {...field}
                                      error={!!error}
                                      helperText={error?.message}
                                      fullWidth
                                      type="text"
                                      label="Expiry Date"
                                    />
                                  )}
                                />
                              ) : (
                                identityDocument?.expiryDate
                                  ?.local()
                                  .format("yyyy-MM-DD") ?? "n/a"
                              )}
                            </Grid>
                            <Grid item xs={6} lg={3}>
                              <Label>Document Type:</Label>
                            </Grid>
                            <Grid item xs={6} lg={9}>
                              {isEditing ? (
                                <Select
                                  label="Type"
                                  onChange={(e) =>
                                    setIdDocType(e.target.value as any)
                                  }
                                  value={idDocType}
                                  fullWidth
                                  options={mapObject(
                                    IdentityFileType.enum,
                                    (title, value) => ({
                                      title,
                                      value,
                                    })
                                  )}
                                />
                              ) : idDocType ? (
                                IdentityFileType.enum[
                                  idDocType as IdentityAttachmentType
                                ]
                              ) : (
                                "n/a"
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                          <Images
                            options={{
                              type: IdentityFileType,
                              category: IdentityFileCategory,
                            }}
                            defaults={{
                              category: IdentityFileCategory.keys[0],
                              type: IdentityFileType.keys[0],
                            }}
                            documents={details.documents}
                            kycId={fraudCheckId}
                          />
                        </Grid>
                      </Grid>
                    </StyledWidget>
                  </Grid>
                  <CanEditKyc>
                    <LeftGridItem item xs={12}>
                      <div>
                        <Button onClick={onIdentityOverlay}>
                          Update Decline Reasons
                        </Button>
                        {documentChanged ? (
                          <RememberToSave>Remember To Save</RememberToSave>
                        ) : null}
                      </div>
                    </LeftGridItem>
                  </CanEditKyc>
                </Grid>
              </ServiceDetail>
            </Grid>
          )
        )}

      {Option(serviceDetails)
        .map((x) => x.find(({ service }) => service === "ADDRESS"))
        .map(({ details, service }) =>
          details.status === "NA" ? null : (
            <Grid item xs={12} key={service}>
              <ServiceDetail status={details.status} changed={addressChanged}>
                <Grid container spacing={3}>
                  <Grid item xs={3}>
                    <Label>{ServiceType.enum[service]}:</Label>
                  </Grid>
                  <Grid item xs={9}>
                    {details.status === "DECLINED" ? (
                      <ChipContainer>
                        {details.declineCodes.map(({ message }, j) => (
                          <Chip key={j} label={message} />
                        ))}
                      </ChipContainer>
                    ) : (
                      <>{details.status === "VERIFIED" ? "OK" : "PENDING"}</>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <StyledWidget title="Uploaded Documents">
                      <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                          <Grid container spacing={3}>
                            <Grid item xs={6}>
                              <Label>Street:</Label>
                            </Grid>
                            <Grid item xs={6}>
                              {isEditing ? (
                                <Controller
                                  control={form.control}
                                  name="userDetails.address.streetAddress"
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => (
                                    <TextField
                                      {...field}
                                      error={!!error}
                                      helperText={error?.message}
                                      fullWidth
                                      type="text"
                                      label="Street"
                                    />
                                  )}
                                />
                              ) : (
                                address?.streetAddress
                              )}
                            </Grid>
                            <Grid item xs={6}>
                              <Label>City:</Label>
                            </Grid>
                            <Grid item xs={6}>
                              {isEditing ? (
                                <Controller
                                  control={form.control}
                                  name="userDetails.address.city"
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => (
                                    <TextField
                                      {...field}
                                      error={!!error}
                                      helperText={error?.message}
                                      fullWidth
                                      type="text"
                                      label="City"
                                    />
                                  )}
                                />
                              ) : (
                                address?.city
                              )}
                            </Grid>
                            <Grid item xs={6}>
                              <Label>State:</Label>
                            </Grid>
                            <Grid item xs={6}>
                              {isEditing ? (
                                <Controller
                                  control={form.control}
                                  name="userDetails.address.state"
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => (
                                    <TextField
                                      {...field}
                                      error={!!error}
                                      helperText={error?.message}
                                      fullWidth
                                      type="text"
                                      label="State"
                                    />
                                  )}
                                />
                              ) : (
                                address?.state
                              )}
                            </Grid>
                            <Grid item xs={6}>
                              <Label>Country:</Label>
                            </Grid>
                            <Grid item xs={6}>
                              {isEditing ? (
                                <Controller
                                  control={form.control}
                                  name="userDetails.address.country"
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => (
                                    <Country
                                      {...field}
                                      error={!!error}
                                      helperText={error?.message}
                                      fullWidth
                                      type="text"
                                      label="Country"
                                    />
                                  )}
                                />
                              ) : address?.country ? (
                                countries.enum[
                                  address.country as typeof countries.T
                                ]
                              ) : null}
                            </Grid>
                            <Grid item xs={6}>
                              <Label>ZIP:</Label>
                            </Grid>
                            <Grid item xs={6}>
                              {isEditing ? (
                                <Controller
                                  control={form.control}
                                  name="userDetails.address.zip"
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => (
                                    <TextField
                                      {...field}
                                      error={!!error}
                                      helperText={error?.message}
                                      fullWidth
                                      type="text"
                                      label="ZIP"
                                    />
                                  )}
                                />
                              ) : (
                                address?.zip
                              )}
                            </Grid>
                            <Grid item xs={6}>
                              <Label>Document Type:</Label>
                            </Grid>
                            <Grid item xs={6}>
                              {isEditing ? (
                                <Select
                                  label="Type"
                                  onChange={(e) =>
                                    setAddDocType(e.target.value as any)
                                  }
                                  value={addDocType}
                                  fullWidth
                                  options={mapObject(
                                    AddressFileType.enum,
                                    (title, value) => ({
                                      title,
                                      value,
                                    })
                                  )}
                                />
                              ) : addDocType ? (
                                AddressFileType.enum[
                                  addDocType as AddressAttachmentType
                                ]
                              ) : (
                                "n/a"
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                          <Images
                            options={{
                              type: AddressFileType,
                              category: AddressFileCategory,
                            }}
                            defaults={{
                              category: AddressFileCategory.keys[0],
                              type: AddressFileType.keys[0],
                            }}
                            documents={details.documents}
                            kycId={fraudCheckId}
                          />
                        </Grid>
                      </Grid>
                    </StyledWidget>
                  </Grid>
                  <CanEditKyc>
                    <LeftGridItem item xs={12}>
                      <div>
                        <Button onClick={onAddressOverlay}>
                          Update Decline Reasons
                        </Button>
                        {addressChanged ? (
                          <RememberToSave>Remember To Save</RememberToSave>
                        ) : null}
                      </div>
                    </LeftGridItem>
                  </CanEditKyc>
                </Grid>
              </ServiceDetail>
            </Grid>
          )
        )}

      {Option(serviceDetails)
        .map((x) => x.find(({ service }) => service === "FACE"))
        .map(({ details, service }) =>
          details.status === "NA" ? null : (
            <Grid item xs={12} lg={6} key={service}>
              <ServiceDetail status={details.status} changed={faceChanged}>
                <Grid container spacing={3}>
                  <Grid item xs={3}>
                    <Label>{ServiceType.enum[service]}:</Label>
                  </Grid>
                  <Grid item xs={9}>
                    {details.status === "DECLINED" ? (
                      <ChipContainer>
                        {details.declineCodes.map(({ message }, j) => (
                          <Chip key={j} label={message} />
                        ))}
                      </ChipContainer>
                    ) : (
                      <>{details.status === "VERIFIED" ? "OK" : "PENDING"}</>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <StyledWidget title="Uploaded Documents">
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Images
                            options={{
                              type: FaceFileType,
                              category: FaceFileCategory,
                            }}
                            defaults={{
                              category: FaceFileCategory.keys[0],
                              type: FaceFileType.keys[0],
                            }}
                            documents={details.documents}
                            kycId={fraudCheckId}
                          />
                        </Grid>
                      </Grid>
                    </StyledWidget>
                  </Grid>
                  <CanEditKyc>
                    <LeftGridItem item xs={12}>
                      <div>
                        <Button onClick={onFaceOverlay}>
                          Update Decline Reasons
                        </Button>
                        {faceChanged ? (
                          <RememberToSave>Remember To Save</RememberToSave>
                        ) : null}
                      </div>
                    </LeftGridItem>
                  </CanEditKyc>
                </Grid>
              </ServiceDetail>
            </Grid>
          )
        )}
      {Option(serviceDetails)
        .map((x) => x.find(({ service }) => service === "CONSENT"))
        .map(({ details, service }) =>
          details.status === "NA" ? null : (
            <Grid item xs={12} key={service}>
              <ServiceDetail status={details.status} changed={consentChanged}>
                <Grid container spacing={3}>
                  <Grid item xs={3}>
                    <Label>{ServiceType.enum[service]}:</Label>
                  </Grid>
                  <Grid item xs={9}>
                    {details.status === "DECLINED" ? (
                      <ChipContainer>
                        {details.declineCodes.map(({ message }, j) => (
                          <Chip key={j} label={message} />
                        ))}
                      </ChipContainer>
                    ) : (
                      <>{details.status === "VERIFIED" ? "OK" : "PENDING"}</>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <StyledWidget title="Uploaded Documents">
                      <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                          <Grid container spacing={3}>
                            <Grid item xs={6}>
                              <Label>Note:</Label>
                            </Grid>
                            <Grid item xs={6}>
                              {consentNote ? consentNote : "n/a"}
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                          <Images
                            options={{
                              type: FaceFileType,
                              category: FaceFileCategory,
                            }}
                            defaults={{
                              category: FaceFileCategory.keys[0],
                              type: FaceFileType.keys[0],
                            }}
                            documents={details.documents}
                            kycId={fraudCheckId}
                          />
                        </Grid>
                      </Grid>
                    </StyledWidget>
                  </Grid>
                  <CanEditKyc>
                    <LeftGridItem item xs={12}>
                      <Button onClick={onConsentOverlay}>
                        Update Decline Reasons
                      </Button>
                    </LeftGridItem>
                  </CanEditKyc>
                </Grid>
              </ServiceDetail>
            </Grid>
          )
        )}
      <Grid item xs={12} lg={6}>
        <Grid container spacing={3}>
          {Option(serviceDetails)
            .map((x) =>
              x.find(({ service }) => service === "BACKGROUND_CHECKS")
            )
            .map(({ details, service }) =>
              details.status === "NA" ? null : (
                <Grid item xs={12} key={service}>
                  <ServiceDetail
                    status={details.status}
                    changed={backgroundChecksChanged}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={3}>
                        <Label>{ServiceType.enum[service]}:</Label>
                      </Grid>
                      <Grid item xs={9}>
                        {details.status === "DECLINED" ? (
                          <ChipContainer>
                            {details.declineCodes.map(({ message }, j) => (
                              <Chip key={j} label={message} />
                            ))}
                          </ChipContainer>
                        ) : (
                          <>
                            {details.status === "VERIFIED" ? "OK" : "PENDING"}
                          </>
                        )}
                      </Grid>
                      <CanEditKyc>
                        <LeftGridItem item xs={12}>
                          <Button onClick={onBackgroundChecksOverlay}>
                            Update Decline Reasons
                          </Button>
                        </LeftGridItem>
                      </CanEditKyc>
                    </Grid>
                  </ServiceDetail>
                </Grid>
              )
            )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ChecksPerformed;
